import { useState, useEffect, useMemo } from 'react';
import { useLocalForageContext } from '../context';
import { getLocalForageStore } from '../utils/localforageStores';
import {
  enrichTasksWithStatus,
  filterTasksByCustomer,
} from '../utils/taskUtils';
import localforage from 'localforage';
import { mergeTaskWithLocalModification } from './useTaskModifications';
import { isSurveyApplicableToCustomer } from '../utils/surveyUtils';
import {
  ACTIVITY_MODE,
  isSurveyRequired,
  isTaskRequired,
} from '../utils/activityPolicy';

const taskModificationsStore = localforage.createInstance({
  name: 'TaskModifications',
});

const EMPTY_SURVEY_STATE = {
  incompleteSurveys: [],
  totalMandatorySurveys: 0,
  totalAllSurveys: 0,
  completedAllSurveysCount: 0,
  totalBatSurveys: 0,
  completedBatSurveysCount: 0,
};

const INITIAL_STATE = {
  loading: true,
  data: [],
  error: null,
  ...EMPTY_SURVEY_STATE,
};

const useAppLocked = (input = {}) => {
  const {
    customer,
    isRemoteVisit,
    activityMode = ACTIVITY_MODE.STANDARD,
  } = input;
  const {
    loadingTasks,
    refetchTasks,
    taskModificationsVersion,
    surveyCompletionVersion,
  } = useLocalForageContext();

  // One state object so a load commits once; React 16 does not batch
  // separate setState calls made after an await.
  const [state, setState] = useState(INITIAL_STATE);

  const tasksStore = getLocalForageStore('Tasks');
  const customersStore = getLocalForageStore('Customers');

  useEffect(() => {
    if (loadingTasks) return undefined;

    let active = true;
    const fetchData = async () => {
      const t0 = performance.now();
      let data = null;
      try {
        const [appLockedData, nodesData, modifications, surveys, completedSession, batMap] = await Promise.all([
          tasksStore.getItem('appLocked'),
          tasksStore.getItem('nodes'),
          taskModificationsStore.getItem('modifications'),
          customersStore.getItem('surveysNodes'),
          customersStore.getItem('completedSurveysSession'),
          customersStore.getItem('batSubjektProducts'),
        ]);
        const tStorage = performance.now();
        if (!active) return;

        let tasks = [];
        if (appLockedData) {
          const localMods = modifications || {};
          const filtered = filterTasksByCustomer(appLockedData, customer);
          const enriched = enrichTasksWithStatus(filtered, nodesData);
          tasks = enriched.map((task) =>
            mergeTaskWithLocalModification(task, localMods[String(task.id)]),
          );
        }
        data = tasks;
        let surveyState = EMPTY_SURVEY_STATE;

        const completedKeys = completedSession || [];
        if (surveys && customer) {
          const customerSurveys = surveys.filter((s) =>
            isSurveyApplicableToCustomer(s, customer),
          );
          const batEligible = (s) => !(
            s.bat === 'D' &&
            batMap &&
            (!batMap[String(customer)] || batMap[String(customer)].length === 0)
          );

          const eligible = customerSurveys.filter(
            (survey) =>
              isSurveyRequired(survey, activityMode, isRemoteVisit) &&
              batEligible(survey),
          );
          const all = customerSurveys.filter(batEligible);
          const bat = all.filter((s) => s.bat === 'D');
          const regular = all.filter((s) => s.bat !== 'D');

          surveyState = {
            totalMandatorySurveys: eligible.length,
            incompleteSurveys: eligible.filter((s) => !completedKeys.includes(`${s.id}_${customer}`)),
            totalAllSurveys: regular.length,
            completedAllSurveysCount: regular.filter((s) => completedKeys.includes(`${s.id}_${customer}`)).length,
            totalBatSurveys: bat.length,
            completedBatSurveysCount: bat.filter((s) => completedKeys.includes(`${s.id}_${customer}`)).length,
          };
        }

        setState((prev) => ({
          ...prev,
          ...surveyState,
          data,
          loading: false,
        }));
        const tEnd = performance.now();
        const storageMs = Math.round(tStorage - t0);
        const enrichMs = Math.round(tEnd - tStorage);
        const totalMs = Math.round(tEnd - t0);
        if (typeof window !== 'undefined' && window.sentryTrackMetric) {
          window.sentryTrackMetric('appLocked_storage_ms', storageMs);
          window.sentryTrackMetric('appLocked_enrich_ms', enrichMs);
          window.sentryTrackMetric('appLocked_total_ms', totalMs);
        }
        if (typeof window !== 'undefined' && window.sentryTrackAction) {
          window.sentryTrackAction('useAppLocked completed', {
            storageMs,
            enrichMs,
            totalMs,
            taskCount: appLockedData?.length || 0,
            surveyCount: surveys?.length || 0,
          });
        }
      } catch (err) {
        if (!active) return;
        setState((prev) => ({
          ...prev,
          ...(data ? { data } : {}),
          error: err,
          loading: false,
        }));
      }
    };

    fetchData();
    return () => {
      active = false;
    };
  }, [
    loadingTasks,
    refetchTasks,
    customer,
    isRemoteVisit,
    activityMode,
    tasksStore,
    customersStore,
    taskModificationsVersion,
    surveyCompletionVersion,
  ]);

  const { data } = state;
  const incompleteTasks = useMemo(
    () =>
      data.filter((task) => {
        const isRepeatableTask = task.type?.code === 'INFOV';
        const hasExecutions = isRepeatableTask && (task.executionCount || 0) > 0;

        if (hasExecutions) {
          return false;
        }

        return !task.isCompleted && isTaskRequired(task, activityMode);
      }),
    [data, activityMode],
  );
  const completedTasks = useMemo(
    () => data.filter((task) => task.isCompleted),
    [data],
  );

  return { ...state, incompleteTasks, completedTasks };
};

export default useAppLocked;
